import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatGpsCoordinates, normalizeGpsPoint } from '../../services/gpsCoordinates';

const DEFAULT_CENTER = [9.0765, 7.3986];
const DEFAULT_ZOOM = 14;

const markerIcon = L.divIcon({
  className: '',
  iconSize: [34, 46],
  iconAnchor: [17, 44],
  popupAnchor: [0, -36],
  html: `
    <div style="position:relative;width:34px;height:46px;display:flex;justify-content:center;align-items:flex-start;">
      <div style="width:28px;height:28px;border-radius:9999px;background:#16a34a;border:3px solid #ffffff;box-shadow:0 10px 24px rgba(15,23,42,0.25);position:relative;">
        <span style="position:absolute;inset:7px;border-radius:9999px;background:rgba(255,255,255,0.9);"></span>
      </div>
      <div style="position:absolute;bottom:0;width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-top:16px solid #16a34a;"></div>
    </div>
  `,
});

function MapFocus({ point }) {
  const map = useMap();

  useEffect(() => {
    if (!point) return;
    map.setView([point.lat, point.lng], Math.max(map.getZoom(), DEFAULT_ZOOM), { animate: true });
  }, [map, point]);

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 0);
    return () => clearTimeout(timer);
  }, [map]);

  return null;
}

function MapClickHandler({ onPick }) {
  useMapEvents({
    click: (event) => {
      onPick({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });

  return null;
}

function GpsCaptureMapModal({
  open,
  point,
  onClose,
  onConfirm,
  onRequestCapture,
  busy,
  error,
}) {
  const [draftPoint, setDraftPoint] = useState(point);

  useEffect(() => {
    setDraftPoint(point);
  }, [point]);

  if (!open) return null;

  const display = formatGpsCoordinates(draftPoint);

  return (
    <div className="fixed inset-0 z-[2600] flex items-center justify-center bg-slate-950/65 px-4 py-6">
      <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-green-600">Capture GPS / waypoint</p>
            <h3 className="mt-1 text-base font-semibold text-slate-900">Confirm the final location</h3>
            <p className="mt-1 text-xs text-slate-500">
              Tap the map or drag the pin. The backend receives GeoJSON as [longitude, latitude].
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close GPS picker"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-4 px-6 py-5 lg:grid-cols-[1.45fr_0.85fr]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <MapContainer
              center={draftPoint ? [draftPoint.lat, draftPoint.lng] : DEFAULT_CENTER}
              zoom={draftPoint ? DEFAULT_ZOOM : 6}
              scrollWheelZoom
              className="h-[420px] w-full"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {draftPoint && (
                <Marker
                  position={[draftPoint.lat, draftPoint.lng]}
                  icon={markerIcon}
                  draggable
                  eventHandlers={{
                    dragend: (event) => {
                      const target = event.target.getLatLng();
                      setDraftPoint({ lat: target.lat, lng: target.lng });
                    },
                  }}
                />
              )}
              <MapFocus point={draftPoint} />
              <MapClickHandler onPick={setDraftPoint} />
            </MapContainer>
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Selected point</p>
              <p className="mt-2 text-lg font-semibold text-slate-900">{display || 'No point selected yet'}</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                The marker can be moved after the browser locates you. Use this to correct the device GPS before saving.
              </p>
            </div>

            <div className="rounded-2xl border border-dashed border-green-200 bg-green-50/70 p-4">
              <p className="text-sm font-semibold text-green-900">Start from your current position</p>
              <p className="mt-2 text-sm leading-6 text-green-900/80">
                If the browser location is slightly off, open the map and nudge the pin to the exact spot.
              </p>
              <button
                type="button"
                onClick={onRequestCapture}
                disabled={busy}
                className="mt-4 inline-flex items-center justify-center rounded-full border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? 'Capturing...' : 'Capture GPS / waypoint'}
              </button>
            </div>

            <div className="mt-auto flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => draftPoint && onConfirm(draftPoint)}
                disabled={!draftPoint}
                className="flex-1 rounded-full bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Use waypoint
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GpsCoordinateField({
  label = 'GPS Coordinates',
  required = false,
  value = '',
  onChange,
  placeholder = 'Tap to capture a waypoint',
  helperText = '',
  disabled = false,
  inputClassName = '',
  buttonClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerError, setPickerError] = useState('');
  const [draftPoint, setDraftPoint] = useState(normalizeGpsPoint(value));

  useEffect(() => {
    setDraftPoint(normalizeGpsPoint(value));
  }, [value]);

  const closePicker = () => {
    setOpen(false);
    setPickerError('');
    setBusy(false);
  };

  const openPicker = (point) => {
    setPickerError('');
    setDraftPoint(point || null);
    setOpen(true);
  };

  const handleCapture = () => {
    if (disabled) return;
    setPickerError('');

    if (!navigator.geolocation) {
      setPickerError('Geolocation is not supported by this browser.');
      return;
    }

    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const point = {
          lat: Number(position.coords.latitude),
          lng: Number(position.coords.longitude),
        };
        openPicker(point);
        setBusy(false);
      },
      (err) => {
        setPickerError(err?.message || 'Unable to capture GPS coordinates.');
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleConfirm = (point) => {
    onChange?.(formatGpsCoordinates(point));
    closePicker();
  };

  const handleInputClick = () => {
    if (disabled) return;
    const current = normalizeGpsPoint(value);
    if (current) {
      openPicker(current);
      return;
    }
    handleCapture();
  };

  const containerClass = inputClassName || 'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-[13px] text-gray-700 placeholder-gray-400 focus:outline-none focus:border-green-400 transition';
  const actionClass = buttonClassName || 'shrink-0 rounded-lg border border-dashed border-green-300 bg-green-50 px-4 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div className="space-y-1.5">
      <label className="block text-[12px] font-semibold text-gray-700">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <div className="flex gap-2">
        <input
          value={formatGpsCoordinates(value)}
          readOnly
          onClick={handleInputClick}
          placeholder={placeholder}
          className={`${containerClass} cursor-pointer`}
        />
        <button type="button" onClick={handleCapture} disabled={disabled || busy} className={actionClass}>
          {busy ? 'Capturing...' : 'Capture GPS / waypoint'}
        </button>
      </div>
      {helperText && <p className="text-[11px] text-gray-400">{helperText}</p>}
      {pickerError && <p className="text-[11px] text-red-600">{pickerError}</p>}
      <GpsCaptureMapModal
        open={open}
        point={draftPoint}
        onClose={closePicker}
        onConfirm={handleConfirm}
        onRequestCapture={handleCapture}
        busy={busy}
        error={pickerError}
      />
    </div>
  );
}
